import React, { useEffect, useState } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import { Video } from '../types';
import { videosService } from '../../../shared/services/videosService';
import SearchableDropdown from '../../../shared/components/ui/SearchableDropdown';
import { useMatches } from '../../../shared/hooks/useMatches';
import { MatchWithTeams } from '../../../shared/types/database';
import { supabase } from '../../../shared/lib/supabase';
import { matchVideosService } from '../../../shared/services/matchVideosService';

interface EditVideoModalProps {
  isOpen: boolean;
  video: Video | null;
  onClose: () => void;
  onSuccess?: (video: Video) => void;
}

const EditVideoModal: React.FC<EditVideoModalProps> = ({ isOpen, video, onClose, onSuccess }) => {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [matchId, setMatchId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMatchLink, setLoadingMatchLink] = useState(false);
  const [matchIdsWithLinkedVideo, setMatchIdsWithLinkedVideo] = useState<Set<string>>(new Set());

  // Fetch matches for match selector
  const { matches, loading: matchesLoading } = useMatches();

  // Fetch match IDs that already have a video linked (for filtering dropdown)
  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      const result = await matchVideosService.getMatchIdsWithLinkedVideo();
      if (result.success) setMatchIdsWithLinkedVideo(new Set(result.data));
      else setMatchIdsWithLinkedVideo(new Set());
    };
    load();
  }, [isOpen]);

  // Load current match link and processing_notes.match_id
  useEffect(() => {
    const loadMatchLink = async () => {
      if (!video) {
        setMatchId('');
        return;
      }

      setLoadingMatchLink(true);
      try {
        // First check processing_notes.match_id (pending link)
        const processingNotes = video.processing_notes as Record<string, any> | null;
        const pendingMatchId = processingNotes?.match_id;

        // Then check if video is already linked to a match via match_videos
        const { data: matchVideos, error: matchVideosError } = await supabase
          .from('match_videos')
          .select('match_id')
          .eq('storage_video_id', video.id)
          .limit(1);

        if (!matchVideosError && matchVideos && matchVideos.length > 0) {
          // Video is already linked
          setMatchId(matchVideos[0].match_id);
        } else if (pendingMatchId) {
          // Video has pending match link in processing_notes
          setMatchId(pendingMatchId);
        } else {
          setMatchId('');
        }
      } catch (err) {
        console.error('Error loading match link:', err);
      } finally {
        setLoadingMatchLink(false);
      }
    };

    loadMatchLink();
  }, [video]);

  useEffect(() => {
    if (video) {
      setDisplayName(video.file_name);
      setDescription(video.description || '');
      setIsActive(video.is_active);
      setError(null);
    } else {
      setDisplayName('');
      setDescription('');
      setIsActive(true);
      setMatchId('');
    }
  }, [video]);

  // Check if video has storage file
  const hasStorageFile = Boolean(video?.storage_path || video?.public_url);

  // Disable is_active toggle if files exist
  const canDeactivate = !hasStorageFile;

  const handleSubmit = async () => {
    if (!video) return;
    if (!displayName.trim()) {
      setError('File name is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Get current match link
      const { data: currentMatchVideos } = await supabase
        .from('match_videos')
        .select('match_id')
        .eq('storage_video_id', video.id)
        .limit(1);
      
      const currentMatchId = currentMatchVideos && currentMatchVideos.length > 0 
        ? currentMatchVideos[0].match_id 
        : null;

      // Update video basic info
      const processingNotes = video.processing_notes as Record<string, any> | null || {};
      const updatedProcessingNotes: Record<string, any> = { ...processingNotes };
      
      // Handle match linking/unlinking
      if (matchId && matchId !== currentMatchId) {
        // Linking to a new match
        if (video.status === 'ready') {
          // Video is transcoded - create match_videos entry immediately
          const videoUrl = video.public_url || '';
          if (!videoUrl) {
            throw new Error('Video public URL not available. Cannot link to match.');
          }

          // Get next video order
          const { data: existingVideos } = await supabase
            .from('match_videos')
            .select('video_order')
            .eq('match_id', matchId)
            .order('video_order', { ascending: false })
            .limit(1);
          
          const nextOrder = existingVideos && existingVideos.length > 0 
            ? (existingVideos[0].video_order || 0) + 1 
            : 1;

          // Create match_videos entry
          const { error: linkError } = await supabase
            .from('match_videos')
            .insert({
              match_id: matchId,
              video_url: videoUrl,
              storage_video_id: video.id,
              video_source_type: 'cloudflare_r2',
              video_order: nextOrder,
              thumbnail_url: null,
              match_start_video_time_seconds: null,
              match_start_match_minute: 0,
              timing_points: [],
            });

          if (linkError) {
            throw new Error(`Failed to link video to match: ${linkError.message}`);
          }

          // Remove match_id from processing_notes since it's now linked
          delete updatedProcessingNotes.match_id;
        } else {
          // Video not transcoded yet - store match_id in processing_notes
          updatedProcessingNotes.match_id = matchId;

          // Update transcode job payload if job exists
          const { data: jobs } = await supabase
            .from('video_processing_jobs')
            .select('id, payload')
            .eq('video_id', video.id)
            .eq('job_type', 'transcode_master')
            .eq('is_active', true)
            .in('status', ['queued', 'running']);

          if (jobs && jobs.length > 0) {
            for (const job of jobs) {
              const updatedPayload = {
                ...job.payload,
                match_id: matchId,
              };
              await supabase
                .from('video_processing_jobs')
                .update({ payload: updatedPayload })
                .eq('id', job.id);
            }
          }
        }
      } else if (!matchId && currentMatchId) {
        // Unlinking from match
        await supabase
          .from('match_videos')
          .delete()
          .eq('storage_video_id', video.id)
          .eq('match_id', currentMatchId);

        // Remove match_id from processing_notes
        delete updatedProcessingNotes.match_id;

        // Remove match_id from transcode job payloads if jobs exist
        const { data: jobs } = await supabase
          .from('video_processing_jobs')
          .select('id, payload')
          .eq('video_id', video.id)
          .eq('job_type', 'transcode_master')
          .eq('is_active', true)
          .in('status', ['queued', 'running']);

        if (jobs && jobs.length > 0) {
          for (const job of jobs) {
            const updatedPayload = { ...job.payload };
            delete updatedPayload.match_id;
            await supabase
              .from('video_processing_jobs')
              .update({ payload: updatedPayload })
              .eq('id', job.id);
          }
        }
      }

      // Update video record
      const finalProcessingNotes = Object.keys(updatedProcessingNotes).length > 0 
        ? updatedProcessingNotes 
        : undefined;

      const result = await videosService.updateVideo(video.id, {
        file_name: displayName.trim(),
        description: description.trim() || null,
        is_active: isActive,
        processing_notes: finalProcessingNotes,
      });

      if (result.success && result.data) {
        onSuccess?.(result.data);
        onClose();
      } else if (result.error) {
        setError(result.error);
      } else {
        setError('Something went wrong while saving changes.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Format match name for display
  const formatMatchName = (match: MatchWithTeams): string => {
    const homeTeam = match.home_team?.name || 'Home Team';
    const awayTeam = match.away_team?.name || 'Away Team';
    const matchDate = match.start_date ? new Date(match.start_date).toLocaleDateString() : '';
    return `${homeTeam} vs ${awayTeam}${matchDate ? ` - ${matchDate}` : ''}`;
  };

  // Prepare match options for dropdown: exclude matches that already have a video linked,
  // except the currently selected match (so user can keep or change selection)
  const matchOptions = matches
    .filter(
      (match) =>
        match.id === matchId || !matchIdsWithLinkedVideo.has(match.id)
    )
    .map((match) => ({
      id: match.id,
      name: formatMatchName(match),
    }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Video" className="max-w-lg">
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">File Name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              if (error) setError(null);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter file name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-gray-400 text-xs">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            placeholder="Add description..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Link to Match <span className="text-gray-400 text-xs">(optional)</span>
          </label>
          <SearchableDropdown
            value={matchId}
            onChange={setMatchId}
            options={matchOptions}
            placeholder="Select a match (optional)"
            loading={matchesLoading || loadingMatchLink}
            showClearIcon={true}
            onClear={() => setMatchId('')}
          />
          <p className="mt-1 text-xs text-gray-500">
            {video?.status === 'ready' 
              ? 'Video is ready. Linking will create match video entry immediately.'
              : 'Video will be linked to match after transcoding completes.'}
          </p>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <p className="text-xs text-gray-500">
              {canDeactivate
                ? 'Inactive videos won\'t block uploads and won\'t be processed'
                : 'Cannot deactivate video with existing files. Delete files first.'}
            </p>
          </div>
          <label className={`flex items-center ${canDeactivate ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => {
                if (canDeactivate) {
                  setIsActive(e.target.checked);
                }
              }}
              disabled={!canDeactivate}
              className="sr-only"
            />
            <div
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isActive ? 'bg-blue-600' : 'bg-gray-300'
              } ${canDeactivate ? '' : 'opacity-50'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </div>
            <span className="ml-3 text-sm font-medium text-gray-700">
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </label>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditVideoModal;

